export const MIN_ENTRIES_FOR_FIRST_RECAP = 3;
export const MIN_NEW_USER_MESSAGES_BETWEEN_RECAPS = 3;

type RecapInputEntry = {
  id: string;
  entryType: string;
  recap?: { throughEntryId: string } | null;
};

export function isConversationalEntry(entry: { entryType: string }) {
  return entry.entryType !== "Recap" && entry.entryType !== "Compaction";
}

// Returns the entry id a new recap should summarize through, or null if one
// isn't warranted yet. Keeps recaps from piling up on intermittent check-ins:
// the first needs a few entries, and each subsequent one needs several new
// user messages since the last recap — so coming back, sending one message,
// and leaving again doesn't spawn another near-identical recap.
export function recapThroughEntryId(
  entries: readonly RecapInputEntry[],
  onDemand = false
): string | null {
  const conversational = entries.filter(isConversationalEntry);
  if (conversational.length === 0) return null;
  if (!onDemand && conversational.length < MIN_ENTRIES_FOR_FIRST_RECAP) {
    return null;
  }
  const latestId = conversational[conversational.length - 1]!.id;

  const coveredId =
    entries.filter((e) => e.entryType === "Recap").at(-1)?.recap
      ?.throughEntryId ?? null;
  if (coveredId === null) return latestId;
  if (onDemand) return coveredId === latestId ? null : latestId;

  const coveredIndex = conversational.findIndex((e) => e.id === coveredId);
  const newUserMessages = conversational
    .slice(coveredIndex + 1)
    .filter((e) => e.entryType === "UserMessage").length;
  if (newUserMessages < MIN_NEW_USER_MESSAGES_BETWEEN_RECAPS) return null;
  return latestId;
}
